import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Modal } from '@/components/Modal';
import { Badge, Button, cx, ErrorState, FIELD_CLASS, Spinner } from '@/components/ui';
import {
  changeColorClass,
  formatKrw,
  formatKrwCompact,
  formatPercent,
  formatSignedNumber,
  formatUsdPrice,
} from '@/lib/format';
import { MoneyInput } from '@/components/MoneyInput';
import { isUsMarket } from './priceDisplay';
import { formatCandleDate } from './candleLabel';
import {
  CHART_INTERVALS,
  CHART_PERIODS,
  type AlertDirection,
  type ChartPeriod,
  type IndicatorSource,
} from './stockTypes';
import {
  useCandles,
  usePriceAlertMutations,
  usePriceAlerts,
  usePrices,
  useStockDetail,
  useUsdKrwRate,
  useWatchlist,
  useWatchlistCategories,
  useWatchlistCategoryItemMutations,
  useWatchlistMutations,
  useDailyPrices,
} from './useStocks';
import { IndicatorGrid } from './components';
import { LadderGuideSection } from './LadderGuideSection';
import { HelpButton } from './HelpButton';
import type { IndicatorHelp } from './indicatorHelp';

/**
 * NXT 배지의 물음표. (2026-09-29 I-57 — 사용자는 "애프터마켓에서 거래되는 종목인지" 를 알고 싶었다)
 *
 * 🔴 배지가 곧 "애프터마켓 가능" 이 아니라는 것을 숫자로 말한다
 *   NXT 지원은 코스피 선별 대상의 42%(9/29 토스 상세 실측)인데, KRX 애프터마켓 거래는 93%(9/16 일봉 실측)에 있었다.
 *   배지가 없으면 애프터마켓에서 못 산다고 읽히지 않게 한다.
 */
const NXT_HELP: IndicatorHelp = {
  label: 'NXT (넥스트레이드)',
  hint: '넥스트레이드에서도 거래되는 종목',
  detail: [
    '넥스트레이드(NXT)는 2025년 3월에 문을 연 대체거래소입니다. 이 배지가 붙은 종목은 한국거래소(KRX)뿐 아니라 NXT에서도 사고팔 수 있습니다.',
    'NXT에서는 정규장 전 08:00~08:50에도 거래됩니다. 4회차부터 안전 추천 계좌가 이 시간에 NXT 종목을 삽니다.',
    '배지가 없다고 애프터마켓에서 못 사는 것은 아닙니다. 2026년 9월 14일부터 한국거래소도 16:00~20:00 애프터마켓을 열어, 9월 16일에는 코스피 선별 대상 종목의 93%에 애프터마켓 거래가 있었습니다(NXT 지원 종목은 42%).',
  ],
};

/** 메모 길이 상한. 백엔드 Watchlist.MEMO_MAX_LENGTH 와 맞춘다 */
const MEMO_MAX_LENGTH = 200;

/**
 * 한 종목에 걸 수 있는 가격 알림 수. (2026-09-02 사용자 확정)
 * 🔴 백엔드 PriceAlertService.MAX_PER_STOCK 과 같아야 한다.
 *   여기가 더 크면 서버가 409(T011)로 거절하는 것을 화면이 허용하는 셈이 된다.
 */
const MAX_ALERTS_PER_STOCK = 5;

/**
 * 종목 상세 모달. (16.2 · 16.4 · 16.5)
 *
 * 검색 결과나 관심종목을 클릭하면 열린다.
 * 한 화면에 차트 · 현재가 · 저점매수 사다리(2026-10-05) · 지표 · 관심종목 토글이 모인다.
 *
 * 🔴 지표의 출처를 반드시 밝힌다
 *   유니버스 밖 종목은 서버가 요청을 받고 그 자리에서 계산한다.
 *   계산식은 저장된 값과 같지만 기준일이 다를 수 있다.
 *   몇 달 뒤에 "이 숫자가 언제 기준인가" 를 알 수 없게 되는 것을 막는다.
 *
 * 🔴 이 숫자들은 전부 일봉으로 계산한 값이다. AI 가 만든 값이 아니다
 *   추천 상세 화면과 같은 원칙이다. (docs/impl/35-trading-screens.md)
 */

/**
 * 차트를 따로 내려받는다.
 *
 * lightweight-charts 가 gzip 약 57KB 다. 이것을 첫 화면 번들에 넣으면
 * 추천 목록만 보려는 사람도 매번 받게 된다. 모달을 여는 순간에만 받는다.
 *
 * 이 모달 자체는 /trading 에 항상 렌더된다(닫혀 있으면 null 을 돌려준다).
 * 그래서 모달을 lazy 로 만들어도 소용이 없고, 차트 컴포넌트를 갈라야 한다.
 */
const CandleChart = lazy(() =>
  import('./CandleChart').then((module) => ({ default: module.CandleChart })),
);

const SOURCE_NOTE: Record<IndicatorSource, string> = {
  STORED: '매일 장 마감 후 일봉으로 계산해 저장한 값입니다.',
  ON_DEMAND:
    '이 종목은 자동 종목 선별 대상이 아니라 저장된 지표가 없습니다. 방금 일봉으로 계산했습니다.',
  NONE: '일봉이 모자라 지표를 계산할 수 없습니다. 신규 상장 종목에서 생깁니다.',
};

/**
 * 기본 차트 — CHART_INTERVALS 의 '일봉' + CHART_PERIODS 의 '3개월'(밀면 3년).
 * ✏️ 2026-09-29 I-70 — 전에는 '3개월' 버튼 하나였다 · 10/9 사용자 "기본은 무조건 3개월" 로 기간 줄을 되살렸다
 *
 * 분봉(0번)을 기본으로 두지 않는다. 모달을 열 때마다 토스 호출이 나가고,
 * 대부분의 경우 먼저 보고 싶은 것은 최근 흐름이다.
 */
const DEFAULT_RANGE_INDEX = 1;
const DEFAULT_PERIOD: ChartPeriod = '3M';

interface Props {
  stockId: number | null;
  onClose: () => void;
}

export function StockDetailModal({ stockId, onClose }: Props) {
  const [rangeIndex, setRangeIndex] = useState(DEFAULT_RANGE_INDEX);
  const [period, setPeriod] = useState<ChartPeriod>(DEFAULT_PERIOD);
  // CHART_INTERVALS 는 상수 배열이지만 인덱스 접근의 타입은 옵셔널이다.
  // 기본값으로 되돌리면 rangeIndex 가 범위를 벗어나는 경우 자체가 없다.
  const range = CHART_INTERVALS[rangeIndex] ?? CHART_INTERVALS[DEFAULT_RANGE_INDEX];
  // 분봉은 하루치뿐이라 기간이 없다(visible null = 전부 보인다)
  const visibleBars = range.visible === null ? null : range.visible[period];

  const detail = useStockDetail(stockId);
  const candles = useCandles(stockId, range.interval, range.count);
  const prices = usePrices(stockId === null ? [] : [stockId]);
  const { add, remove } = useWatchlistMutations();

  // 미국 종목일 때만 원화 병기용 환율을 읽는다 (2026-08-20).
  // 훅은 조건 밖에서 부르고 enabled 로 요청 여부만 가른다.
  const usStock = detail.data ? isUsMarket(detail.data.market) : false;
  const exchangeRate = useUsdKrwRate(usStock);

  const price = prices.data?.[0];

  /*
    배경 스크롤 잠금과 ESC 는 components/Modal 로 옮겼다. (2026-08-17)
    두 벌로 두면 한쪽만 고쳤을 때 모달마다 동작이 달라진다.
  */

  // 다른 종목을 열면 봉 단위 · 기간 선택을 기본(일봉 · 3개월)으로 돌린다.
  // 남겨두면 분봉을 보다 다른 종목을 열었을 때 토스 호출이 바로 나간다.
  useEffect(() => {
    setRangeIndex(DEFAULT_RANGE_INDEX);
    setPeriod(DEFAULT_PERIOD);
  }, [stockId]);

  if (stockId === null) {
    return null;
  }

  const stock = detail.data;
  const watched = stock?.watched ?? false;

  function toggleWatch() {
    if (stockId === null) {
      return;
    }
    if (watched) {
      remove.mutate(stockId);
    } else {
      add.mutate({ stockId });
    }
  }

  return (
    /*
      껍데기(배경 스크롤 잠금 · ESC · 배경 클릭)는 components/Modal 이 맡는다.
      2026-08-17 에 관리자 회원 수정이 같은 것을 필요로 해서 공통으로 옮겼다.
    */
    <Modal open onClose={onClose} maxWidth="sm:max-w-3xl">
      <>
        {detail.isLoading && <Spinner label="종목 정보를 불러오는 중" />}

        {detail.isError && (
          <div className="p-4">
            <ErrorState
              message={detail.error?.message ?? '종목을 불러오지 못했습니다.'}
              onRetry={() => void detail.refetch()}
            />
          </div>
        )}

        {stock && (
          <>
            {/* ── 헤더 ── */}
            <div className="sticky top-0 z-10 flex items-start gap-3 border-b border-line bg-surface px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="truncate text-lg font-semibold text-fg">
                    {stock.name}
                  </h2>
                  <span className="font-mono text-sm text-fg-muted">{stock.symbol}</span>
                  <Badge tone="neutral">{stock.market}</Badge>
                  {/*
                    NXT 배지 (2026-09-19 사용자 요청 — 애프터마켓 거래 여부를 알고 싶다).
                    🔴 글자를 "애프터마켓" 이 아니라 "NXT" 로 둔 이유
                      토스가 주는 값은 nxtSupported(넥스트레이드 지원)뿐이다. 2026-09-14 부터
                      KRX 본장도 16~20시 경쟁매매를 열어서, NXT 미지원 종목도 그쪽에서는 거래된다.
                      실측(9/15→9/16 일봉)에서 코스피 804종목 중 749종목(93%)에 애프터마켓
                      거래대금이 있었다 — "애프터마켓" 이라고 쓰면 나머지 종목에 거짓이 된다.
                      값이 채워진 뒤 두 비율을 견줘 문구를 다시 본다 (BACKLOG I-57).
                    ✅ 9/29 실측(토스 상세 직접 조회) — 코스피 선별 대상 803종목 중 NXT 335(41.7%).
                      93% 와 두 배 넘게 달라 "NXT" 그대로 두고, 물음표로 차이를 설명한다.
                      (그 전까지 배지가 한 번도 안 뜬 것은 저장 누락이었다 — StockMasterSyncService)
                  */}
                  {stock.nxtSupported === true && (
                    <span className="inline-flex items-center">
                      <Badge tone="info">NXT</Badge>
                      <HelpButton help={NXT_HELP} />
                    </span>
                  )}
                  {stock.securityType !== 'STOCK' && (
                    <Badge tone="info">{stock.securityType}</Badge>
                  )}
                  {stock.status === 'DELISTED' && <Badge tone="danger">상장폐지</Badge>}
                </div>

                {/*
                  업종. 종목명 아래·가격 위에 둔다 (2026-09-10 사용자 요청).
                  둘 이상일 수 있어 배지를 나열하고, 좁은 화면에서 줄바꿈되게 wrap 한다.
                  미분류면 아무것도 그리지 않는다 - 빈 자리가 생기는 편이
                  "미분류" 라는 글자가 매번 보이는 것보다 낫다.
                */}
                {/*
                  🔴 시가총액을 업종 배지와 같은 줄에 둔다 (2026-09-12 사용자 요청)
                    섹터 화면의 열을 없애고 이리로 옮겼다. 자리를 여기로 정한 이유는
                    성격이 업종과 같아서다 - "이 회사가 어떤 회사인가" 이지
                    "지금 살까" 가 아니다. 가격(PriceLine) 위에 두어 판단하는 숫자와
                    알아보는 숫자를 가른다.

                    flex-wrap 이라 폰에서 업종이 여럿이면 자연히 다음 줄로 내려간다.
                    배지와 섞이지 않게 라벨(시총)을 붙였다.
                */}
                {(stock.sectors.length > 0 || stock.marketCap !== null) && (
                  <div className="mt-1 flex flex-wrap items-center gap-1">
                    {stock.sectors.map((sector) => (
                      <span
                        key={sector}
                        className="rounded bg-surface-muted px-1.5 py-0.5 text-[11px] leading-tight text-fg-muted"
                      >
                        {sector}
                      </span>
                    ))}
                    {stock.marketCap !== null && (
                      <span className="rounded bg-surface-muted px-1.5 py-0.5 text-[11px] leading-tight text-fg-muted">
                        시총{' '}
                        <span className="tabular text-fg">{formatKrwCompact(stock.marketCap)}</span>
                      </span>
                    )}
                  </div>
                )}

                <PriceLine
                  us={isUsMarket(stock.market)}
                  lastPrice={price?.lastPrice ?? stock.closePrice}
                  changeRate={price?.changeRate ?? null}
                  change={price?.change ?? null}
                  prevCloseDate={price?.prevCloseDate ?? null}
                  stale={price?.stale ?? true}
                  fallbackDate={stock.latestTradeDate}
                  hasLivePrice={price !== undefined}
                  usdKrwRate={exchangeRate.data?.rate ?? null}
                  rateFetchedAt={exchangeRate.data?.fetchedAt ?? null}
                />
              </div>

              <div className="flex shrink-0 items-center gap-2">
                <Button
                  variant={watched ? 'secondary' : 'primary'}
                  onClick={toggleWatch}
                  disabled={add.isPending || remove.isPending}
                >
                  {watched ? '관심종목에서 빼기' : '관심종목에 담기'}
                </Button>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="닫기"
                  className="rounded p-1 text-fg-subtle hover:bg-surface-hover hover:text-fg-muted"
                >
                  ✕
                </button>
              </div>
            </div>

            {add.isError && (
              <p className="px-4 pt-3 text-sm text-danger">{add.error?.message}</p>
            )}

            {/* ── 관심종목 설정 (3.10) ── */}
            {watched && (
              <WatchlistSettings
                stockId={stockId}
                currentPrice={price?.lastPrice ?? stock.closePrice ?? null}
              />
            )}

            {/* ── 차트 ── */}
            <section className="px-4 py-4">
              {/* 윗줄 = 봉 크기 */}
              <div className="mb-1.5 flex flex-wrap gap-1">
                {CHART_INTERVALS.map((option, index) => (
                  <button
                    key={option.label}
                    type="button"
                    aria-pressed={index === rangeIndex}
                    onClick={() => setRangeIndex(index)}
                    className={cx(
                      'rounded-full px-3 py-1 text-xs font-medium',
                      index === rangeIndex
                        ? 'bg-brand text-brand-fg'
                        : 'bg-surface-hover text-fg-muted',
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              {/*
                아랫줄 = 처음 보이는 기간 (2026-10-09 사용자 요청 · stockTypes CHART_PERIODS 주석)
                  분봉은 하루치라 기간이 없다 — 버튼을 숨기지 않고 흐리게 둬 줄 높이가 흔들리지 않게 한다
              */}
              <div className="mb-2 flex flex-wrap gap-1">
                {CHART_PERIODS.map((option) => {
                  const active = range.visible !== null && option.key === period;
                  return (
                    <button
                      key={option.key}
                      type="button"
                      aria-pressed={active}
                      disabled={range.visible === null}
                      onClick={() => setPeriod(option.key)}
                      className={cx(
                        'rounded-full border px-3 py-0.5 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-40',
                        active
                          ? 'border-brand text-brand'
                          : 'border-line text-fg-muted',
                      )}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>

              {/* 분봉은 토스를 부르므로 눈에 띄게 느리다. 로딩을 반드시 보여준다. */}
              {candles.isLoading && (
                <Spinner
                  label={
                    range.interval === '1m'
                      ? '분봉을 받는 중 (조금 걸립니다)'
                      : '차트를 불러오는 중'
                  }
                />
              )}

              {candles.isError && (
                <ErrorState
                  message={candles.error?.message ?? '차트를 불러오지 못했습니다.'}
                  onRetry={() => void candles.refetch()}
                />
              )}

              {candles.data && candles.data.length === 0 && (
                <p className="py-8 text-center text-sm text-fg-muted">
                  {range.interval === '1m'
                    ? '분봉 데이터가 없습니다. 장이 열린 적 없는 종목이거나 거래정지 상태입니다.'
                    : range.interval === '1d'
                      ? '일봉이 아직 수집되지 않았습니다.'
                      : '주봉 · 월봉은 우리가 모은 일봉을 묶어 만듭니다. 이 종목은 모은 일봉이 아직 없습니다.'}
                </p>
              )}

              {candles.data && candles.data.length > 0 && (
                <Suspense fallback={<Spinner label="차트를 준비하는 중" />}>
                  <CandleChart
                    candles={candles.data}
                    interval={range.interval}
                    height={300}
                    us={isUsMarket(stock.market)}
                    visibleBars={visibleBars}
                  />
                </Suspense>
              )}

              {range.interval === '1m' && (
                <p className="mt-2 text-xs text-fg-subtle">
                  분봉은 저장하지 않고 조회할 때마다 받아옵니다. 최대 하루치입니다.
                </p>
              )}
              {/*
                주봉 · 월봉 안내 (2026-09-29 I-70 · 9/20 사용자 결정 "안내 문구를 붙인다")
                  토스는 주봉 · 월봉을 주지 않아 우리 일봉을 묶는다. 가진 기간은 종목마다 달라(3~10년 · 신규 상장)
                  숫자를 박지 않고 받은 첫 봉 날짜로 그린다(CLAUDE.md 6절 9항)
              */}
              {range.interval !== '1m' && candles.data && candles.data.length > 0 && (
                <p className="mt-2 text-xs text-fg-subtle">
                  옆으로 밀면 과거가, 두 손가락으로 벌리면 확대됩니다.
                  {range.interval !== '1d' &&
                    ` 주봉 · 월봉은 토스가 주지 않아 우리가 모은 일봉을 한 주(월~금) · 한 달씩 묶었습니다 — ` +
                    `이 차트의 첫 봉은 ${formatCandleDate(candles.data[0]?.date)}${range.interval === '1w' ? '(주봉은 최근 약 3년)' : ''}부터입니다. ` +
                    (isUsMarket(stock.market)
                      ? '미국 일봉은 정규장 가격만 담아 주봉 · 월봉도 정규장 기준입니다.'
                      : '일봉이 시간외까지 합친 통합 봉이라 주봉 · 월봉도 같습니다.')}
                </p>
              )}
            </section>

            {/*
              ── 저점매수 사다리 (2026-10-05 사용자 결정 · BACKLOG I-89 · docs/impl/138 18.8) ──
              차트와 기술적 지표 사이(사용자 지정). 국내만 - 미국 종목은 일봉이 DB 에 없을 수 있어 서버도 계산하지 않는다
            */}
            {!usStock && (
              <LadderGuideSection
                stockId={stockId}
                currentPrice={price?.lastPrice ?? stock.closePrice ?? null}
                watched={watched}
              />
            )}

            {/* ── 지표 ── */}
            <section className="border-t border-line px-4 py-4">
              <div className="mb-2 flex items-center gap-2">
                <h3 className="text-sm font-semibold text-fg">기술적 지표</h3>
                {stock.indicatorSource === 'ON_DEMAND' && (
                  <Badge tone="warning">즉석 계산</Badge>
                )}
              </div>

              <p className="mb-3 text-xs text-fg-muted">
                {SOURCE_NOTE[stock.indicatorSource]} 이 숫자들은 일봉으로 계산한 값이며
                AI가 만든 값이 아닙니다.
              </p>

              {stock.indicator ? (
                <IndicatorGrid indicator={stock.indicator} />
              ) : (
                <p className="text-sm text-fg-muted">
                  보유한 일봉이 {stock.candleCount}개라 계산할 수 없습니다.
                </p>
              )}

              {!stock.universe && stock.excludeReason && (
                <p className="mt-3 text-xs text-fg-muted">
                  자동 종목 선별 대상이 아닙니다 — {stock.excludeReason}
                </p>
              )}
            </section>

            {/* ── 일자별 시세 (2026-08-28 사용자 요청) ── */}
            <DailyPriceSection stockId={stockId} us={usStock} />
          </>
        )}
      </>
    </Modal>
  );
}

/**
 * 일자별 시세 표 - 증권사 앱의 "일자별" 과 같은 것. (2026-08-28)
 *
 * 스크롤이 바닥에 닿으면 다음 30줄을 이어 받는다 (IntersectionObserver).
 * observer 를 지원하지 않거나 놓치는 경우를 위해 "더 보기" 버튼을 함께 둔다.
 *
 * 🔴 최대 1년까지만 내려간다 - 경계는 서버가 자른다 (사용자 요구사항).
 * 🔴 미국 종목은 일봉이 DB 에 안 쌓여 이 표가 비어 있을 수 있다 (Phase B 전).
 */
function DailyPriceSection({ stockId, us }: { stockId: number; us: boolean }) {
  const daily = useDailyPrices(stockId);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = daily;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting) && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      // 바닥에 닿기 한 화면쯤 전에 미리 받기 시작한다 - 스크롤이 안 끊긴다
      { rootMargin: '200px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const rows = daily.data?.pages.flatMap((page) => page.rows) ?? [];

  return (
    <section className="border-t border-line px-4 py-4">
      <h3 className="text-sm font-semibold text-fg">일자별 시세</h3>
      {/*
        🔴 네이버·증권사와 종가가 다를 수 있다 (2026-09-20 사용자 지적)
          우리 일봉은 토스의 통합 봉이라 애프터마켓(~20:00)까지 합친 마지막 값이 종가다.
          그 사실을 안 적으면 "우리 숫자가 틀렸다" 로 읽힌다.

        🔴 2026-09-20 — 이 문단이 미국 종목에도 그대로 나오고 있었다
          `us` 를 받고 있으면서 쓰는 곳이 빈 상태 문구뿐이었다. AAPL 을 열어도
          "정규장 09:00~15:30" 이 떠서, 미국 종가를 국내 애프터마켓 기준으로 읽게 된다.

        🔴 그리고 미국은 날짜 기준이 다르다
          td_daily_candle.trade_date 의 US 행은 **ET(미국 현지) 날짜**다
          (MarketCalendarService·DailyCandleSyncJob 주석). "2026-09-19" 행은 실제로는
          KST 09-19 22:30 ~ 09-20 05:00 세션이다. 밝히지 않으면 국내 행과 하루가 어긋난다.
      */}
      <p className="mb-2 text-xs text-fg-subtle">
        {us ? (
          <>
            <strong>일자는 미국 현지(ET) 날짜</strong>입니다 — 한국시간으로는 그날 밤에서 다음날
            새벽에 걸친 장입니다. 증권사가 보여주는 값과 다를 수 있습니다.
          </>
        ) : (
          <>
            정규장(09:00~15:30)과 시간외(~20:00)를 합친 값입니다. 증권사가 보여주는 정규장 종가와
            다를 수 있습니다.
          </>
        )}
      </p>

      {daily.isLoading && <Spinner label="일자별 시세를 불러오는 중" />}

      {rows.length === 0 && !daily.isLoading && (
        <p className="text-sm text-fg-muted">
          {us
            ? /*
                 🔴 2026-09-19 백필로 132종목은 일봉이 들어왔다. 그 밖의 미국 종목은 아직 없다 (I-17)

                 🔴 2026-09-20 — "감시 대상 132종목" 이라고 적었던 것을 고쳤다
                   ① 132 는 **일봉을 받는** 대상이지 감시 대상이 아니다. 측정·매매에 쓰는 것은
                      거래대금 $1억 이상인 83종목이다(StockRepository.findUsTradable).
                   ② "감시 대상" 은 이 앱에서 국내 저점매수 전용 용어다(평일 21:00 재선정되는 100종목).
                      미국에는 그런 것이 아직 없는데, 저 문구는 "미국에도 감시 대상이 굴러간다" 로 읽힌다.
              */
              '이 미국 종목은 일봉을 아직 받아 두지 않았습니다. 거래가 활발한 일부 종목만 받아 두었습니다.'
            : '일봉이 아직 없습니다.'}
        </p>
      )}

      {/*
        🔴 여기에 자체 스크롤(max-h + overflow)을 두지 않는다 (2026-08-28 사용자 요청)

        모달 껍데기가 이미 max-h-[92vh] overflow-y-auto 다. 안쪽에 또 스크롤
        상자를 두면 스크롤바가 두 개 겹쳐 보여 어느 것을 굴려야 할지 헷갈린다.
        모달 하나만 스크롤하고 표는 그대로 길어진다.

        같은 이유로 thead 에 sticky 를 붙이지 않는다 - 스크롤 주체가 모달이라
        모달 헤더(sticky top-0 z-10)와 같은 자리를 다투다 헤더 뒤로 숨는다.
      */}
      {rows.length > 0 && (
        /*
          🔴 가로 스크롤을 준다 (2026-08-31)
            [대비] 열에 "-1,200 (-2.35%)" 같은 긴 값이 들어가는데 숫자라
            줄바꿈되지 않는다(.tabular 이 nowrap). 좁은 화면에서 모달 밖으로
            잘려 나가던 것을 표 안에서 스크롤되게 한다.
         */
        <div className="overflow-x-auto">
          <table className="w-full min-w-max text-sm">
            <thead>
              <tr className="text-xs text-fg-subtle">
                <th className="py-1.5 text-left font-medium">일자</th>
                <th className="py-1.5 text-right font-medium">종가</th>
                <th className="py-1.5 text-right font-medium">대비</th>
                <th className="py-1.5 text-right font-medium">거래량</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.date} className="border-t border-line">
                  <td className="py-1.5 tabular text-fg-muted">{row.date}</td>
                  <td className={cx('py-1.5 text-right tabular', changeColorClass(row.changeRate))}>
                    {us ? formatUsdPrice(row.close) : formatKrw(row.close)}
                  </td>
                  <td className={cx('py-1.5 text-right tabular text-xs', changeColorClass(row.changeRate))}>
                    {row.change === null ? (
                      '-'
                    ) : (
                      <>
                        {formatSignedNumber(row.change)}
                        {row.changeRate !== null && ` (${formatPercent(row.changeRate)})`}
                      </>
                    )}
                  </td>
                  <td className="py-1.5 text-right tabular text-xs text-fg-muted">
                    {row.volume === null ? '-' : row.volume.toLocaleString('ko-KR')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* 바닥 감지선. 이게 보이면 다음 페이지를 받는다 */}
          <div ref={sentinelRef} />

          {isFetchingNextPage && <Spinner label="이어서 불러오는 중" />}

          {hasNextPage && !isFetchingNextPage && (
            <Button
              variant="ghost"
              fullWidth
              onClick={() => void fetchNextPage()}
              className="mt-1"
            >
              더 보기
            </Button>
          )}

          {!hasNextPage && (
            <p className="mt-2 text-center text-xs text-fg-subtle">
              최대 1년까지 제공합니다.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * 관심종목 메모와 카테고리. (3.10)
 *
 * 담아둔 종목일 때만 보인다.
 *
 * 🔴 메모 API 는 16단계부터 있었지만 부르는 화면이 없었다
 *   목록이 메모를 보여주기만 하고 입력할 곳이 없어, 사실상 죽은 엔드포인트였다.
 *   카테고리를 붙이는 자리가 바로 그 자리라 함께 살린다.
 *
 * 값을 종목 상세가 아니라 관심종목 목록에서 읽는 이유
 *   메모와 카테고리는 관심종목의 속성이지 종목의 속성이 아니다.
 *   StockDetailResponse 에 복사해 두면 담기·빼기 때마다 두 응답을 함께 무효화해야 하고,
 *   한쪽만 갱신되면 화면에 남아 있는 값이 틀린다.
 *   목록은 이미 캐시에 있고(staleTime 5분), 없으면 한 번 받아온다.
 */
function WatchlistSettings({
  stockId,
  currentPrice,
}: {
  stockId: number;
  /** 현재가. 알림 줄이 "지금보다 얼마나 위/아래인지" 를 말하는 데 쓴다 */
  currentPrice: number | null;
}) {
  const watchlist = useWatchlist();
  const categories = useWatchlistCategories();
  const { updateMemo } = useWatchlistMutations();
  const { addItem, removeItem } = useWatchlistCategoryItemMutations();

  const entry = watchlist.data?.find((item) => item.stockId === stockId);

  const [memo, setMemo] = useState('');

  /*
    목록이 늦게 오거나 다른 종목으로 바뀌면 입력칸을 그 값으로 맞춘다.

    watchlistId 를 의존성으로 쓰는 이유
      entry 객체는 목록을 다시 받을 때마다 새 참조가 되어, entry 를 그대로 쓰면
      15초 시세 갱신과 무관한 재조회에도 사용자가 치던 메모가 되돌아간다.
  */
  useEffect(() => {
    setMemo(entry?.memo ?? '');
  }, [entry?.watchlistId]);

  if (!entry) {
    // 담기 직후 목록을 다시 받는 사이다. 자리만 비워 두면 화면이 덜컥거린다.
    return null;
  }

  const dirty = memo.trim() !== (entry.memo ?? '');

  return (
    <section className="flex flex-wrap items-center gap-2 border-b border-line bg-surface-muted px-4 py-3">
      {categories.data && categories.data.length > 0 && (
        /*
          🔴 체크박스다. 하나만 고르는 것이 아니다
            한 종목이 여러 카테고리에 들어갈 수 있다. select 로 두면
            하나를 고르는 순간 다른 하나가 풀린 것처럼 보인다.
        */
        <div className="flex w-full flex-wrap items-center gap-x-3 gap-y-1">
          <span className="text-xs text-fg-subtle">카테고리</span>
          {categories.data.map((category) => {
            const checked = entry.categories.some(
              (ref) => ref.categoryId === category.id,
            );
            return (
              <label
                key={category.id}
                className="flex items-center gap-1 text-sm text-fg"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={addItem.isPending || removeItem.isPending}
                  onChange={() => {
                    const target = { categoryId: category.id, stockId };
                    if (checked) {
                      removeItem.mutate(target);
                    } else {
                      addItem.mutate(target);
                    }
                  }}
                />
                {category.name}
              </label>
            );
          })}
        </div>
      )}

      <input
        value={memo}
        onChange={(event) => setMemo(event.target.value)}
        maxLength={MEMO_MAX_LENGTH}
        placeholder="왜 담았는지 적어두면 몇 주 뒤에 도움이 됩니다"
        aria-label="메모"
        className={cx(FIELD_CLASS, 'min-w-0 flex-1')}
      />

      <Button
        variant="secondary"
        onClick={() => updateMemo.mutate({ stockId, memo: memo.trim() })}
        disabled={!dirty || updateMemo.isPending}
      >
        메모 저장
      </Button>

      {(updateMemo.isError || addItem.isError || removeItem.isError) && (
        <p className="w-full text-sm text-danger">
          {updateMemo.error?.message ??
            addItem.error?.message ??
            removeItem.error?.message}
        </p>
      )}

      <PriceAlertRow stockId={stockId} market={entry.market} currentPrice={currentPrice} />
    </section>
  );
}

/**
 * 가격 알림 영역. (2026-08-20 · 2026-09-02 여러 개 지원)
 *
 * "이 가격까지 내려오면 산다 / 여기까지 오르면 판다" 를 미리 정해두면
 * 서버가 거래 시간 내내 15초마다 검사해, 기준 도달 순간 폰 알림과 Slack 으로 알린다.
 * 저점이 아닌 자리에서 충동 매수하는 것을 막기 위한 장치라, 알림은
 * "네가 정한 가격에 왔다" 까지만 말한다. 사고파는 판단은 사용자의 것이다.
 *
 * 🔴 한 종목에 최대 5개 (2026-09-02 사용자 요청, V123)
 *   "220,000 에 하나, 200,000 에 하나" 같은 사다리를 만들 수 있다.
 *   위·아래를 합쳐 5개다 - 방향별로 세면 한 종목에 10개까지 걸려
 *   그 종목이 크게 움직이는 날 같은 종목 알림이 연달아 온다.
 *
 * 🔴 걸어둔 알림을 이 자리에 나열한다
 *   전에는 안내 문구 한 줄뿐이라 무엇을 걸어뒀는지 모달에서 알 수 없었다.
 *   지금은 입력칸 아래에 행으로 쌓이고, 기준가가 높은 순이다 -
 *   현재가를 가운데 두고 위아래로 읽힌다.
 *
 * 🔴 발동하면 꺼진다. 다시 걸려면 같은 값으로 저장하면 된다.
 *   안 끄면 기준 아래에 머무는 동안 15초마다 알림이 온다.
 *
 * 입력·표시가 통화를 따른다 (2026-08-20 미국 지원)
 *   국내는 MoneyInput(콤마 자동)이다. 미국은 센트(소수)가 필요해
 *   MoneyInput(정수 전용)을 못 쓰고 맨 입력을 유지한다.
 */
function PriceAlertRow({
  stockId,
  market,
  currentPrice,
}: {
  stockId: number;
  market: string | null;
  /** 현재가. 알림 줄이 "지금보다 얼마나 위/아래인지" 를 말하는 데 쓴다 */
  currentPrice: number | null;
}) {
  const alerts = usePriceAlerts();
  const { set, removeOne } = usePriceAlertMutations();
  const us = isUsMarket(market);

  const [direction, setDirection] = useState<AlertDirection>('BELOW');
  const [input, setInput] = useState('');

  /*
    이 종목에 걸린 알림. 기준가가 높은 순으로 세운다.
    현재가를 가운데 둔 사다리로 읽히려면 위쪽(목표)이 먼저 와야 한다.
  */
  const mine = (alerts.data ?? [])
    .filter((entry) => entry.stockId === stockId)
    .sort((a, b) => b.targetPrice - a.targetPrice);

  const parsed = Number(input.replaceAll(',', ''));
  const valid = Number.isFinite(parsed) && parsed > 0;
  const full = mine.length >= MAX_ALERTS_PER_STOCK;
  // 같은 자리에 이미 있으면 새로 만들지 않고 다시 켜는 것이라 상한과 무관하다
  const duplicate = mine.some(
    (entry) => entry.direction === direction && entry.targetPrice === parsed,
  );
  const money = us ? formatUsdPrice : formatKrw;

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-fg-subtle">가격 알림</span>

        {/* 저점(사는 자리) · 목표(파는 자리) */}
        <div className="flex gap-1">
          {(
            [
              { key: 'BELOW', label: '저점 ↓' },
              { key: 'ABOVE', label: '목표 ↑' },
            ] as const
          ).map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => setDirection(option.key)}
              className={cx(
                'rounded-full px-2 py-1 text-xs',
                option.key === direction
                  ? 'bg-brand text-brand-fg'
                  : 'bg-surface-hover text-fg-muted',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>

        {us ? (
          <input
            value={input}
            onChange={(event) => setInput(event.target.value)}
            inputMode="decimal"
            placeholder="기준단가 ($)"
            aria-label="가격 알림 기준단가"
            className={cx(FIELD_CLASS, 'w-32')}
          />
        ) : (
          <MoneyInput
            value={input}
            onChange={setInput}
            placeholder="기준단가 (원)"
            aria-label="가격 알림 기준단가"
            className="w-32"
          />
        )}

        <Button
          variant="secondary"
          onClick={() => {
            if (!valid) return;
            set.mutate(
              { stockId, targetPrice: parsed, direction },
              // 저장하면 입력칸을 비운다 - 다음 알림을 바로 이어 걸 수 있다
              { onSuccess: () => setInput('') },
            );
          }}
          disabled={!valid || set.isPending || (full && !duplicate)}
        >
          {duplicate ? '다시 걸기' : '알림 추가'}
        </Button>

        <span className="text-xs text-fg-subtle tabular">
          {mine.length} / {MAX_ALERTS_PER_STOCK}
        </span>
      </div>

      {/*
        안내 문구. 걸어둔 알림이 없을 때만 자세히 말한다 -
        알림이 쌓이면 그 목록 자체가 설명이라, 문구는 짧게 줄인다.
      */}
      {mine.length === 0 ? (
        <p className="text-xs text-fg-muted">
          {/* ✏️ 9/28 I-78 2차 — Slack 은 관리자만(I-82) · 시간외 15분 무체결이면 안 울림(QUOTE_FRESH_MINUTES) */}
          기준가를 정해두면 <strong>폰 알림</strong>으로 알려드립니다(관리자는 Slack 도).
          {us
            ? // 🔴 2026-09-20 — "한국시간 밤" 은 정규장 어감이라 좁았다.
            //   RealtimePriceJob 은 isTradingHours("US") 로 판정하고 그 쿼리는
            //   프리마켓(17:00)부터 애프터마켓(~08:50)까지 전부 본다. 같은 날
            //   RecommendationListPage 도 같은 이유로 고쳤는데 이 줄만 안 따라왔다.
            ' 미국 시장이 열려 있는 동안(프리마켓·애프터마켓 포함, 한국시간 오후~다음날 아침) 15초마다 검사합니다.'
            : ' 정규장과 시간외를 포함해 그 종목이 거래되는 시간 내내 15초마다 검사합니다.'}{' '}
          시간외에 15분 넘게 체결이 없으면 울리지 않습니다. 한 종목에 {MAX_ALERTS_PER_STOCK}개까지
          걸 수 있습니다.
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {mine.map((entry) => (
            <li
              key={entry.id}
              className={cx(
                'flex items-center justify-between gap-2 rounded-lg px-2 py-1.5',
                'bg-surface-muted',
                !entry.active && 'opacity-60',
              )}
            >
              <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <Badge tone={entry.direction === 'BELOW' ? 'info' : 'success'}>
                  {entry.directionLabel}
                </Badge>
                <span className="tabular text-sm font-medium text-fg">
                  {money(entry.targetPrice)}
                </span>
                <span className="text-xs text-fg-subtle">
                  {alertGapNote(entry.targetPrice, currentPrice)}
                </span>
                {!entry.active && (
                  <span className="text-xs text-fg-subtle">
                    울림
                    {entry.triggeredPrice !== null && <> · 당시 {money(entry.triggeredPrice)}</>}
                    {' — 같은 값으로 저장하면 다시 켜집니다'}
                  </span>
                )}
              </span>
              <button
                type="button"
                onClick={() => removeOne.mutate({ alertId: entry.id })}
                disabled={removeOne.isPending}
                aria-label={`${money(entry.targetPrice)} 알림 삭제`}
                className="shrink-0 rounded px-1 text-xs text-fg-muted underline hover:text-fg"
              >
                삭제
              </button>
            </li>
          ))}
        </ul>
      )}

      {full && (
        <p className="text-xs text-fg-subtle">
          {MAX_ALERTS_PER_STOCK}개를 모두 썼습니다. 새로 걸려면 하나를 지우세요.
        </p>
      )}

      {(set.isError || removeOne.isError) && (
        <p className="w-full text-sm text-danger">
          {set.error?.message ?? removeOne.error?.message}
        </p>
      )}
    </div>
  );
}

/**
 * 기준가가 현재가에서 얼마나 떨어져 있나. 알림 줄 옆에 조용히 붙는다.
 *
 * 부호를 그대로 쓴다 - 현재가보다 위면 +, 아래면 −. 방향(저점/목표)은
 * 배지가 이미 말하고 있어서 여기서 또 뒤집으면 읽는 사람이 헷갈린다.
 */
function alertGapNote(target: number, current: number | null): string {
  if (current === null || current === 0) {
    return '';
  }
  const gap = ((target - current) / current) * 100;
  return `현재가 대비 ${formatPercent(gap)}`;
}

/**
 * 현재가 한 줄.
 *
 * 🔴 실시간 시세와 마지막 종가를 구분해 보여준다
 *   장이 닫혀 있거나 시세를 못 받았으면 "종가" 라고 명시한다.
 *   둘을 같은 모양으로 보여주면 주말에 본 값을 실시간으로 착각한다.
 *
 * 🔴 등락은 우리가 계산한 값이다
 *   토스 현재가 API 는 전일종가를 주지 않는다. td_daily_candle 이 기준이고,
 *   일봉 수집이 밀리면 기준일이 그저께가 된다. 그래서 기준일을 함께 적는다.
 */
function PriceLine({
  lastPrice,
  change,
  changeRate,
  prevCloseDate,
  stale,
  fallbackDate,
  hasLivePrice,
  us,
  usdKrwRate,
  rateFetchedAt,
}: {
  /** 미국 종목이면 달러로 표기한다 (2026-08-20) */
  us: boolean;
  lastPrice: number | null;
  change: number | null;
  changeRate: number | null;
  prevCloseDate: string | null;
  stale: boolean;
  fallbackDate: string | null;
  hasLivePrice: boolean;
  /** USD → KRW 환율. 미국 종목의 원화 병기에 쓴다. 아직 없으면 null */
  usdKrwRate: number | null;
  /** 환율 기준 시각. 매시 정각 갱신이라 밝히지 않으면 실시간으로 오해한다 */
  rateFetchedAt: string | null;
}) {
  if (lastPrice === null) {
    return <p className="mt-1 text-sm text-fg-muted">가격 정보가 없습니다</p>;
  }

  return (
    <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <span className="text-xl font-semibold text-fg tabular">
        {us ? formatUsdPrice(lastPrice) : formatKrw(lastPrice)}
      </span>

      {changeRate !== null && (
        <span className={cx('text-sm font-medium', changeColorClass(changeRate))}>
          {change !== null &&
            `${change > 0 ? '+' : ''}${us ? formatUsdPrice(change) : formatKrw(change)} `}
          ({formatPercent(changeRate)})
        </span>
      )}

      {/*
        🔴 "종가" 가 정규장 종가라는 뜻이 아니다 (2026-09-20 사용자 지적)
          우리 일봉은 토스의 **통합 봉** - 정규장(~15:30)과 애프터마켓(~20:00)을 합친 값이라
          마지막 체결가가 종가로 들어간다. 그래서 네이버가 대표로 보여주는 정규장 종가와
          몇백 원씩 다를 수 있다(달바글로벌 9/18 - 정규장 182,100 · 애프터마켓 182,200).
          그냥 "종가" 라고만 적으면 우리 숫자가 틀린 것으로 읽힌다.
          2026-09-19 에 일부러 통합 봉을 쓰기로 한 결정이다 (I-52) - 화면이 그 사실을 말한다.
        🔴 미국은 반대다 — 일봉 종가가 **정규장 종가**다 (2026-09-30 실측 · I-78)
          9/28(ET) AAPL · AMZN · MSFT · NVDA · TSLA 다섯 종목의 DB 일봉 종가가 모두 토스 1분봉
          16:00 봉 종가와 같았고 애프터마켓 마지막 봉과는 모두 달랐다. 시간외 봉 가격은 일봉 고저 밖으로도
          나갔다(고가 초과 4/5 · 저가 미만 5/5) — 토스 미국 일봉은 정규장만 담는다. 국내 근거로 쓴
          "시간외 포함" 을 미국에 그대로 붙이면 거짓이 된다.
      */}
      <span className="text-xs text-fg-subtle">
        {!hasLivePrice
          ? `${fallbackDate ?? '-'} ${us ? '종가(정규장)' : '종가(시간외 포함)'}`
          : stale
            ? '장 마감'
            : '실시간'}
        {prevCloseDate && ` · ${prevCloseDate} 종가 대비`}
      </span>

      {/*
        원화 병기 (2026-08-20 사용자 요청).
        참고용 표시 환율(매시 정각 저장)이라 "약" 과 기준 시각을 붙인다.
        환율이 아직 없으면(배포 직후 첫 배치 전) 병기를 숨긴다.
      */}
      {us && usdKrwRate !== null && (
        <span className="w-full text-xs text-fg-muted">
          약 {formatKrw(lastPrice * usdKrwRate)}
          {rateFetchedAt && ` · 환율 ${rateFetchedAt.slice(11, 16)} 기준`}
        </span>
      )}
    </div>
  );
}
